/**
 * Parser ligero para la búsqueda global (Entrega 4.3).
 *
 * Soporta operadores:
 *   - `>500€` o `>500`     → importe total mayor que 500
 *   - `<100€` o `<100`     → importe total menor que 100
 *   - `100-200€` o `100-200` → rango (ambos inclusive)
 *   - `nif:B12345678`      → NIF exacto (case-insensitive, sin espacios ni guiones)
 *   - `prov:marivuelta`    → substring sobre el nombre del proveedor
 *
 * Cualquier otro token se considera búsqueda libre (substring sobre el resto
 * de campos).
 *
 * El parser es defensivo: si un token no encaja en ningún operador, se trata
 * como texto libre.
 */

export interface ParsedQuery {
  /** Texto libre concatenado en minúsculas (substring sobre searchValue). */
  freeText: string;
  /** Filtro por NIF normalizado en MAYÚSCULAS (sin espacios/puntos/guiones). */
  nif: string | null;
  /** Substring case-insensitive sobre el nombre del proveedor. */
  supplier: string | null;
  /** Importe mínimo (inclusive). */
  amountMin: number | null;
  /** Importe máximo (inclusive). */
  amountMax: number | null;
}

const EMPTY: ParsedQuery = {
  freeText: "",
  nif: null,
  supplier: null,
  amountMin: null,
  amountMax: null,
};

const stripCurrency = (raw: string): string => raw.replace(/[€\s]/g, "");

const toNumber = (raw: string): number | null => {
  if (!raw) return null;
  const cleaned = raw.replace(/\./g, "").replace(",", ".");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

const normalizeNif = (raw: string): string =>
  raw.toUpperCase().replace(/[\s.\-_/]/g, "").replace(/^ES(?=[A-Z0-9]{9}$)/, "");

export const parseSearchQuery = (input: string): ParsedQuery => {
  if (!input || !input.trim()) return EMPTY;

  // Tokenize respecting "key:value" and quoted phrases.
  const tokens = input.match(/"[^"]*"|\S+/g) || [];

  const freeParts: string[] = [];
  let nif: string | null = null;
  let supplier: string | null = null;
  let amountMin: number | null = null;
  let amountMax: number | null = null;

  for (const rawToken of tokens) {
    const token = rawToken.replace(/^"|"$/g, "");
    if (!token) continue;

    // key:value operators
    const colonIdx = token.indexOf(":");
    if (colonIdx > 0 && colonIdx < token.length - 1) {
      const key = token.slice(0, colonIdx).toLowerCase();
      const value = token.slice(colonIdx + 1);
      if (key === "nif" || key === "cif" || key === "dni") {
        nif = normalizeNif(value);
        continue;
      }
      if (key === "prov" || key === "proveedor") {
        supplier = value.toLowerCase();
        continue;
      }
    }

    // amount operators: >N, <N, N-M  (con € opcional)
    const stripped = stripCurrency(token);

    if (stripped.startsWith(">=")) {
      const n = toNumber(stripped.slice(2));
      if (n !== null) { amountMin = n; continue; }
    }
    if (stripped.startsWith("<=")) {
      const n = toNumber(stripped.slice(2));
      if (n !== null) { amountMax = n; continue; }
    }
    if (stripped.startsWith(">")) {
      const n = toNumber(stripped.slice(1));
      if (n !== null) {
        // estrictamente mayor → sumamos el menor incremento posible representable
        amountMin = n + 0.01;
        continue;
      }
    }
    if (stripped.startsWith("<")) {
      const n = toNumber(stripped.slice(1));
      if (n !== null) {
        amountMax = n - 0.01;
        continue;
      }
    }
    // Range "N-M"  (ambos numéricos, "-" en medio)
    const rangeMatch = stripped.match(/^(\d+(?:[.,]\d+)?)-(\d+(?:[.,]\d+)?)$/);
    if (rangeMatch) {
      const lo = toNumber(rangeMatch[1]);
      const hi = toNumber(rangeMatch[2]);
      if (lo !== null && hi !== null) {
        amountMin = Math.min(lo, hi);
        amountMax = Math.max(lo, hi);
        continue;
      }
    }

    freeParts.push(token.toLowerCase());
  }

  return {
    freeText: freeParts.join(" ").trim(),
    nif,
    supplier,
    amountMin,
    amountMax,
  };
};

export interface MatchableInvoice {
  searchValue: string;     // ya en minúsculas
  supplier: string;        // como aparece, lo normalizamos al comparar
  taxId: string;           // como aparece, lo normalizamos al comparar
  totalAmount: number | null;
}

export const matchesQuery = (item: MatchableInvoice, q: ParsedQuery): boolean => {
  if (q.nif) {
    if (normalizeNif(item.taxId || "") !== q.nif) return false;
  }
  if (q.supplier) {
    if (!(item.supplier || "").toLowerCase().includes(q.supplier)) return false;
  }
  if (q.amountMin !== null) {
    if (item.totalAmount === null || item.totalAmount < q.amountMin) return false;
  }
  if (q.amountMax !== null) {
    if (item.totalAmount === null || item.totalAmount > q.amountMax) return false;
  }
  if (q.freeText) {
    // Soporta múltiples palabras: todas deben aparecer (AND).
    const parts = q.freeText.split(/\s+/).filter(Boolean);
    for (const p of parts) {
      if (!item.searchValue.includes(p)) return false;
    }
  }
  return true;
};

/** True si el query no contiene ningún filtro útil. */
export const isEmptyQuery = (q: ParsedQuery): boolean =>
  !q.freeText && !q.nif && !q.supplier && q.amountMin === null && q.amountMax === null;
